import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import SEO from './SEO';

const CategoryPage = ({ isDarkMode }) => {
  const { categoryName } = useParams();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCategoryArticles = async () => {
      setLoading(true);
      try {
        const q = query(collection(db, 'articles'), where('category', '==', categoryName));
        const querySnapshot = await getDocs(q);
        const articlesList = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        articlesList.sort((a, b) => (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0));
        setArticles(articlesList);
      } catch (error) {
        console.error("კატეგორიის ჩატვირთვის შეცდომა:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchCategoryArticles();
  }, [categoryName]);

  const getCategoryIcon = (category) => {
    const icons = {
      'AI': '🤖',
      'ტექნოლოგია': '💻',
      'პროგრამირება': '👨‍💻',
      'ვებ-დეველოპმენტი': '🌐',
      'მობილური აპლიკაციები': '📱',
      'კიბერუსაფრთხოება': '🔒',
      'მეცნიერება': '🔬',
      'ბიზნესი': '💼',
    };
    return icons[category] || '📄';
  };

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${
        isDarkMode ? 'bg-gray-900' : 'bg-gray-55'
      }`}>
        <p className="text-center animate-pulse text-xl">იტვირთება...</p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen pb-12 transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900 text-gray-100' : 'bg-gray-55 text-gray-900'
    }`}>
      
      <SEO 
        title={`${categoryName} - სტატიები`}
        description={`${categoryName} - უახლესი სტატიები და სიახლეები`}
        path={`/category/${categoryName}`}
      />

      {/* Navigation */}
      <nav className={`py-4 px-6 shadow-sm ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <div className="max-w-6xl mx-auto flex items-center gap-4">
          <button onClick={() => navigate('/')} className="text-blue-500 hover:text-blue-600 font-bold">
            ← მთავარი
          </button>
          <span className="text-gray-400">/</span>
          <span className="font-medium">{categoryName}</span>
        </div>
      </nav>

      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-12 mt-4">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="text-6xl mb-4">{getCategoryIcon(categoryName)}</div>
          <h1 className="text-4xl md:text-5xl font-bold mb-2">{categoryName}</h1>
          <p className="text-blue-100 text-lg">
            ყველა სტატია {categoryName}-ს შესახებ
          </p>
          <p className="text-blue-200 text-sm mt-2">
            📄 {articles.length} სტატია
          </p>
        </div>
      </header>

      {/* Articles Grid */}
      <main className="max-w-6xl mx-auto px-4 mt-10">
        {articles.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500">😔 ამ კატეგორიაში სტატიები ჯერ არ არის</p>
            <button onClick={() => navigate('/')} className="mt-4 text-blue-500 hover:underline">
              მთავარ გვერდზე დაბრუნება
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article) => (
              <article key={article.id} className={`group rounded-2xl overflow-hidden border transition-all duration-300 hover:-translate-y-1 hover:shadow-xl ${
                isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'
              }`}>
                {article.imageUrl && (
                  <div className="h-48 overflow-hidden relative">
                    <img src={article.imageUrl} alt={article.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <span className="absolute top-3 left-3 bg-black/60 backdrop-blur-md text-white text-xs font-bold px-2.5 py-1 rounded-md">
                      {getCategoryIcon(article.category)} {article.category}
                    </span>
                  </div>
                )}
                <div className="p-5">
                  <h3 onClick={() => navigate(`/article/${article.id}`)} className="text-lg font-bold line-clamp-2 cursor-pointer hover:text-blue-500 transition-colors mb-2">
                    {article.title}
                  </h3>
                  <p className={`text-sm line-clamp-3 mb-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {article.summary}
                  </p>
                  <div className="flex justify-between items-center pt-2 border-t border-gray-500/10">
                    <span className="text-xs text-gray-500">
                      🕒 {article.createdAt?.seconds ? new Date(article.createdAt.seconds * 1000).toLocaleDateString('ka-GE') : 'ახლახან'}
                    </span>
                    <button onClick={() => navigate(`/article/${article.id}`)} className="text-xs font-bold text-blue-500 hover:text-blue-600">
                      წაკითხვა →
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default CategoryPage;